import React, { useEffect, useState } from 'react';
import { dispatch, useSelector } from '../../../redux/store';
import { PATH_DASHBOARD } from '../../../routes/paths';
import useSettings from '../../../hooks/useSettings';
import Page from '../../../components/Page';
import Iconify from '../../../components/Iconify';
import HeaderBreadcrumbs from '../../../components/HeaderBreadcrumbs';
import DataGridTable from '../../../components/table/DataGridTable';
import { LoadingButton } from '@mui/lab';
import { toast } from 'react-toastify';
import { capitalize, utcMoment } from '../../../utils/common';
import { useParams } from 'react-router-dom';
import { addFood, getAllFoods, getFoods, getFoodsUnits, updateFood, updateFoodStock } from '../../../redux/slices/stockManagementRedux/foodRedux';
import Timeline from '@mui/lab/Timeline';
import TimelineItem from '@mui/lab/TimelineItem';
import TimelineOppositeContent from '@mui/lab/TimelineOppositeContent';
import TimelineSeparator from '@mui/lab/TimelineSeparator';
import TimelineConnector from '@mui/lab/TimelineConnector';
import TimelineDot from '@mui/lab/TimelineDot';
import TimelineContent from '@mui/lab/TimelineContent';
import { Autocomplete, Box, Button, Card, Container, Drawer, Grid, IconButton, Stack, SwipeableDrawer, TextField, Tooltip, Typography } from '@mui/material';
import { FOOD_CATEGORIES } from '../../../utils/constants';

const FoodList = () => {
  const { paramType } = useParams();
  const capitalizedType = capitalize(paramType);
  const pageName = `${capitalizedType} List`;
  const pageNameSingular = `${capitalizedType} List`;
  const { themeStretch } = useSettings();
  const dataModel = { id: '', name: '', description: '', stockQty: 0, cost: 0, price: 0, unitId: null, type: '', foodBible: [] };
  const [selectedDataObj, setSelectedDataObj] = useState(dataModel);
  const [isAdd, setIsAdd] = useState(false);
  const [ingredientHistoryDrawer, setIngredientHistoryDrawer] = useState(false);
  const [isModelOpen, setIsModelOpen] = useState(false);
  const [foodList, setFoodList] = useState([]);
  const [ingredientList, setIngredientList] = useState([]);
  const [rowSelection, setRowSelection] = useState(false);
  const [logData, setLogData] = useState([]);
  const { allData, data, addData, units } = useSelector((state) => state?.food);
  const [editingStockQty, setEditingStockQty] = useState(null);
  const [editRowId, setEditRowId] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    dispatch(getFoodsUnits());
  }, []);

  useEffect(() => {
    dispatch(getFoods(paramType));
    if (paramType === 'menu') {
      dispatch(getAllFoods());
    }
  }, [paramType]);

  useEffect(() => {
    setFoodList(data?.map((value) => ({ ...value, unit: value?.unit?.name })));
    setIsModelOpen(false);
  }, [data]);

  useEffect(() => {
    setIngredientList(allData?.filter((value) => value?.type === 'ingredient'));
  }, [allData]);

  const manageModel = (modelData, type) => {
    if (isModelOpen) {
      setIsModelOpen(false);
    } else {
      setIsModelOpen(true);
      setIsAdd(type === 'add');
      if (type === 'add') {
        setSelectedDataObj({ id: '', name: '', description: '', stockQty: 0, cost: 0, price: 0, unitId: null, type: paramType, foodBible: [] });
      } else {
        setSelectedDataObj({ ...modelData, type: paramType });
      }
    }
  };

  useEffect(() => {
    if (addData?.data) {
      dispatch(getFoods(paramType));
    }
  }, [addData]);

  const updateEditingData = (key, value) => {
    setSelectedDataObj({ ...selectedDataObj, [key]: value });
  };

  const columns = !foodList?.[0]
    ? []
    : Object.keys(foodList?.[0])
      .map((value) => {
        if (['seo', 'logs', 'unitId', 'foodBible'].includes(value)) {
          return {};
        } else if (['price', 'category', 'cost'].includes(value) && paramType === 'ingredient') {
          return {};
        } else if (value === 'stockQty' && paramType === 'menu') {
          return {};
        } else if (value === 'stockQty') {
          return {
            accessorKey: value,
            header: capitalize(value),
            Cell: ({ row }) => {
              return (
                <>
                  {editRowId === row?.original?.id ? (
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <TextField
                        fullWidth
                        type="number"
                        value={editingStockQty}
                        onChange={(e) => setEditingStockQty(e.target.value)}
                        size="small"
                        sx={{ width: 150 }}
                        autoFocus
                        onClick={() => {
                          const input = document.querySelector('input');
                          input?.select();
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            setFoodList((prevState) =>
                              prevState.map((v) => {
                                if (v?.id === row?.original?.id) {
                                  return { ...v, stockQty: editingStockQty };
                                }
                                return v;
                              })
                            );
                            setEditRowId(null);
                            dispatch(updateFoodStock({ id: row?.original?.id, stockQty: editingStockQty }));
                            toast.success('Stock quantity updated!');
                          }
                        }}
                      />
                    </Box>
                  ) : (
                    <Typography
                      onClick={() => {
                        setEditingStockQty(row?.original?.stockQty);
                        setEditRowId(row?.original?.id);
                      }}
                      sx={{ cursor: 'pointer' }}
                    >
                      {row?.original?.stockQty}
                    </Typography>
                  )}
                </>
              );
            },
          };
        } else {
          return { accessorKey: value, header: capitalize(value) };
        }
      })
      .filter((v) => v?.accessorKey);

  const handleSubmit = () => {
    if (selectedDataObj.name.length === 0) {
      toast.error('Name is required!');
    } else if (selectedDataObj.description.length === 0) {
      toast.error('Description is required!');
    } else {
      dispatch(isAdd ? addFood(selectedDataObj) : updateFood(selectedDataObj));
    }
  };

  const handleSoftDelete = async () => {
    if (!selectedDataObj?.id) return;
    const ok = window.confirm('Mark this item as deleted?');
    if (!ok) return;
    try {
      setDeleting(true);
      await dispatch(updateFood({ ...selectedDataObj, isDeleted: true, type: paramType }));
      toast.success('Item marked as deleted');
      setIsModelOpen(false);
      dispatch(getFoods(paramType));
    } catch {
      toast.error('Failed to delete');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Page title={`${pageName}`}>
      <Container maxWidth={themeStretch ? false : 'lg'}>
        <HeaderBreadcrumbs
          heading={`${pageName}`}
          links={[{ name: 'Dashboard', href: PATH_DASHBOARD.root }, { name: 'Product', href: '' }, { name: `${pageName}` }]}
          action={
            <Button variant="contained" startIcon={<Iconify icon="eva:plus-fill" />} onClick={() => manageModel(dataModel, 'add')}>
              New {`${pageName}`}
            </Button>
          }
        />

        <DataGridTable
          name={`${pageName} List`}
          data={foodList}
          column={columns}
          onRowClick={() => {}}
          isRowClickable={false}
          isLoading={false}
          rowSelection={rowSelection}
          setRowSelection={setRowSelection}
          enableRowSelection={false}
          enableRowActions={true}
          renderRowActionItems={(row) => [
            <>
              <Tooltip title={`Edit`}>
                <IconButton color={'warning'} size="small" onClick={() => manageModel(row?.original, 'edit')}>
                  <Iconify icon={'line-md:edit-twotone'} />
                </IconButton>
              </Tooltip>
              <Tooltip title={`View History`}>
                <IconButton
                  style={{ color: '#00a39e' }}
                  size="small"
                  onClick={() => {
                    setLogData(row?.original?.logs);
                    setIngredientHistoryDrawer(true);
                  }}
                >
                  <Iconify icon={'material-symbols:history'} />
                </IconButton>
              </Tooltip>
            </>,
          ]}
        />

        <SwipeableDrawer
          anchor={'top'}
          open={isModelOpen}
          onOpen={() => setIsModelOpen(true)}
          onClose={() => setIsModelOpen(false)}
          PaperProps={{ sx: { width: '90%', height: '90%', marginLeft: '5%', marginTop: '3%', borderRadius: '10px' } }}
        >
          <Box sx={{ p: 2 }}>
            <h1>{isAdd ? 'Add' : 'Edit'} {pageNameSingular}</h1>
          </Box>
          <Grid container spacing={1}>
            <Grid item xs={12} md={8}>
              <Stack spacing={1} sx={{ p: 1 }}>
                <Card sx={{ p: 3 }}>
                  <Typography variant="subtitle1" padding="10px 0">
                    {`${pageNameSingular}`} Information
                  </Typography>
                  <Stack spacing={3}>
                    <TextField fullWidth label="Name" value={selectedDataObj?.name} onChange={(e) => updateEditingData('name', capitalize(e.target.value))} />
                    <TextField fullWidth label="Description" value={selectedDataObj?.description} onChange={(e) => updateEditingData('description', capitalize(e.target.value))} />
                    {paramType === 'menu' && <TextField fullWidth label="Cost" value={selectedDataObj?.cost} onChange={(e) => updateEditingData('cost', capitalize(e.target.value))} />}
                    {paramType === 'menu' && <TextField fullWidth label="Price" value={selectedDataObj?.price} onChange={(e) => updateEditingData('price', capitalize(e.target.value))} />}
                    <Autocomplete
                      onChange={(event, newValue) => {
                        updateEditingData('unitId', units.find((v) => v?.id === newValue?.value)?.id);
                      }}
                      options={units?.map((v) => ({ label: `${v?.name}`, value: v?.id }))}
                      value={{ label: `${units?.find((v) => v.id === selectedDataObj?.unitId)?.name || 'Select Unit'}`, value: selectedDataObj?.unitId }}
                      renderInput={(params) => <TextField label="Select Unit" {...params} />}
                    />
                    {paramType === 'menu' && (
                      <Autocomplete
                        onChange={(event, newValue) => {
                          updateEditingData('category', newValue?.value);
                        }}
                        options={FOOD_CATEGORIES}
                        value={{ label: `${FOOD_CATEGORIES?.find((v) => v.value === selectedDataObj?.category)?.label || 'Select Category'}`, value: selectedDataObj?.category }}
                        renderInput={(params) => <TextField label="Select Category" {...params} />}
                      />
                    )}
                  </Stack>
                </Card>
              </Stack>
            </Grid>

            <Grid item xs={12} md={4}>
              <Stack spacing={1} sx={{ p: 1 }}>
                <Card sx={{ p: 3 }}>
                  <Stack spacing={2} sx={{ p: 3 }}>
                    <LoadingButton type="submit" variant="contained" size="large" style={{ width: '100%' }} onClick={handleSubmit}>
                      {isAdd ? `Add ${pageNameSingular}` : `Save ${pageNameSingular}`}
                    </LoadingButton>
                    {!isAdd && (
                      <LoadingButton loading={deleting} color="error" variant="outlined" size="large" style={{ width: '100%' }} onClick={handleSoftDelete} startIcon={<Iconify icon="mdi:trash-can-outline" />}>
                        Delete
                      </LoadingButton>
                    )}
                    <Button color="info" variant="outlined" size="large" style={{ width: '100%' }} onClick={() => manageModel(dataModel, 'add')}>
                      Close
                    </Button>
                  </Stack>
                </Card>
              </Stack>
            </Grid>

            {paramType === 'menu' && (
              <Grid item xs={12} md={12}>
                <Stack spacing={1} sx={{ p: 1 }}>
                  <Card sx={{ p: 3 }}>
                    <Typography variant="subtitle1" padding="10px 0">
                      Food Bible
                    </Typography>
                    <Stack spacing={3}>
                      <Autocomplete
                        onChange={(event, newValue) => {
                          setSelectedDataObj((prevState) => {
                            const exists = prevState.foodBible?.some((item) => item.ingredientId === newValue?.value);
                            if (!exists && newValue?.value) {
                              return { ...prevState, foodBible: [...prevState.foodBible, { ingredientId: newValue?.value, qty: 0 }] };
                            }
                            return prevState;
                          });
                        }}
                        options={ingredientList?.map((v) => ({ label: v?.name, value: v?.id }))}
                        renderInput={(params) => <TextField label="Select Ingredient" {...params} />}
                      />
                      {selectedDataObj?.foodBible?.map((ingredient) => {
                        const ingredientData = ingredientList.find((ing) => ing?.id === ingredient.ingredientId);
                        const unitData = units?.find((unit) => unit?.id === ingredientData?.unitId);
                        return (
                          ingredientData && (
                            <TextField
                              key={ingredient.ingredientId}
                              fullWidth
                              type="number"
                              label={`${ingredientData?.name} (${unitData?.name})`}
                              value={ingredient.qty}
                              onChange={(e) => {
                                setSelectedDataObj((prevState) => {
                                  const updated = prevState.foodBible.map((item) => (item.ingredientId === ingredient.ingredientId ? { ...item, qty: parseFloat(e.target.value) } : item));
                                  return { ...prevState, foodBible: updated };
                                });
                              }}
                            />
                          )
                        );
                      })}
                    </Stack>
                  </Card>
                </Stack>
              </Grid>
            )}
          </Grid>
        </SwipeableDrawer>

        <Drawer anchor="right" open={ingredientHistoryDrawer} onClose={() => setIngredientHistoryDrawer(false)}>
          <Box sx={{ width: 400, padding: 2 }}>
            <Typography variant="h6" gutterBottom>
              Ingredient History
            </Typography>
            <div style={{ maxHeight: '90vh', overflowY: 'auto' }}>
              <Timeline position="alternate">
                {logData?.map((value) => (
                  <TimelineItem key={value.id}>
                    <TimelineOppositeContent sx={{ m: 'auto 0' }} align="right" variant="body2" color="text.secondary">
                      {utcMoment(value?.datetime).format('YYYY-MM-DD HH:mm:ss')}
                    </TimelineOppositeContent>
                    <TimelineSeparator>
                      <TimelineConnector />
                      <TimelineDot>
                        <Iconify icon="eva:checkmark-circle-2-fill" />
                      </TimelineDot>
                      <TimelineConnector />
                    </TimelineSeparator>
                    <TimelineContent sx={{ py: '12px', px: 2 }}>
                      <Typography variant="body1" component="span">
                        <b>{value?.title}</b>
                      </Typography>
                    </TimelineContent>
                  </TimelineItem>
                ))}
              </Timeline>
            </div>
          </Box>
        </Drawer>
      </Container>
    </Page>
  );
};

export default FoodList;
